import { useCallback } from 'react';
import { useBalance } from '@hooks/api/useBalance';
import { useUnlockAdditionalContacts } from '@hooks/common/useUnlockAdditionalContacts';
import { get } from 'lodash-es';

import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CampaignIcon from '@mui/icons-material/Campaign';
import CategoryIcon from '@mui/icons-material/Category';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import LinkIcon from '@mui/icons-material/Link';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import StarIcon from '@mui/icons-material/Star';
import { Avatar, Box, Chip, Divider, Drawer, IconButton, Rating, Typography } from '@mui/material';
import { AdditionalContacts } from '@components/AdditionalContacts';
import { LeadStatusBadge } from '@components/LeadStatusBadge';
import { useTheme } from '@components/theme-v2/ThemeContext';

interface LeadDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  lead: Record<string, any> | null;
}

const InfoRow = ({
  icon,
  label,
  value,
  color = '#00d4aa',
  copyable = false,
  isDark,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | undefined | null;
  color?: string;
  copyable?: boolean;
  isDark: boolean;
}) => {
  const displayValue = value || 'N/A';
  const hasValue = value && value !== 'N/A';

  const handleCopy = useCallback(() => {
    if (hasValue) {
      navigator.clipboard.writeText(value!);
    }
  }, [value, hasValue]);

  // Pre-compute colors based on isDark prop
  const labelColor = isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.55)';
  const valueColor = hasValue
    ? isDark
      ? '#ffffff'
      : '#1a1f2e'
    : isDark
      ? 'rgba(255,255,255,0.5)'
      : 'rgba(0,0,0,0.38)';
  const hoverBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
  const iconBtnColor = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.38)';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        py: 1.5,
        px: 2,
        borderRadius: 1,
        '&:hover': {
          backgroundColor: hoverBg,
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '8px',
          backgroundColor: `${color}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: labelColor,
            textTransform: 'uppercase',
            fontSize: '0.7rem',
            letterSpacing: '0.5px',
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: valueColor,
            wordBreak: 'break-word',
            fontWeight: hasValue ? 500 : 400,
          }}
        >
          {displayValue}
        </Typography>
      </Box>
      {copyable && hasValue && (
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{
            color: iconBtnColor,
            '&:hover': { color: '#00d4aa' },
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

const SectionHeader = ({ title }: { title: string }) => (
  <Box sx={{ px: 2, pt: 3, pb: 1 }}>
    <Typography
      variant="overline"
      sx={{
        color: '#00d4aa',
        fontWeight: 600,
        letterSpacing: '1px',
        fontSize: '0.75rem',
      }}
    >
      {title}
    </Typography>
  </Box>
);

export const LeadDetailsDrawer: React.FC<LeadDetailsDrawerProps> = ({ open, onClose, lead }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { unlockSingleLead } = useUnlockAdditionalContacts();
  const { data: balanceData } = useBalance();
  const userCredits = balanceData?.token ?? balanceData?.balance ?? 0;

  if (!lead) return null;

  // Helper to get data from lead
  const getData = (key: string): any => get(lead, key) || get(lead, `data.${key}`) || get(lead, `raw.${key}`) || '';

  // Clean value helper
  const cleanValue = (value: unknown): string => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'object' && !Array.isArray(value)) return '';
    if (typeof value === 'number' && Number.isNaN(value)) return '';
    if (value === 'NaN' || value === 'undefined' || value === 'null') return '';
    return String(value);
  };

  const name = cleanValue(getData('name'));
  const phone = cleanValue(getData('phone'));
  const emailRaw = getData('email');
  const email = Array.isArray(emailRaw) ? emailRaw[0] : cleanValue(emailRaw);
  const street = cleanValue(getData('street')) || cleanValue(getData('address'));
  const city = cleanValue(getData('city'));
  const state = cleanValue(getData('state'));
  const zipCode = cleanValue(getData('zip_code')) || cleanValue(getData('zip'));
  const website = cleanValue(getData('website')) || cleanValue(getData('url'));
  const source = cleanValue(getData('source'));
  const sourceUrl = cleanValue(getData('source_url'));
  const rating = getData('rating');
  const categories = getData('categories');
  const campaignName = cleanValue(getData('campaign_name'));
  const scraperName = cleanValue(getData('scraper_name'));
  const createdAt = cleanValue(getData('created_at'));
  const scrapedAt = cleanValue(getData('scraped_at'));
  const company = cleanValue(getData('company')) || name;
  const positionRaw = getData('position');
  const position = cleanValue(positionRaw) && positionRaw !== 'NaN' ? cleanValue(positionRaw) : '';
  const linkedinUrl = cleanValue(getData('linkedin_url'));
  const reviewCount = cleanValue(getData('review_count'));

  // Extract additional contacts from additionalData
  const additionalData = lead.additionalData || lead.additional_data || lead.data?.additionalData || {};
  const additionalContacts = additionalData.additional_contacts || [];
  const additionalEmails: string[] = additionalData.additional_emails || [];
  const additionalPhones: string[] = additionalData.additional_phones || [];
  const additionalContactsUnlocked = additionalData.additional_contacts_unlocked || false;
  const leadId = cleanValue(getData('id')) || cleanValue(getData('leads_id')) || '';

  // Build full address
  const addressParts = [street, city, state, zipCode].filter(Boolean);
  const fullAddress = addressParts.join(', ') || '';

  // Parse rating
  const ratingValue =
    typeof rating === 'string' ? parseFloat(rating) : typeof rating === 'number' ? rating : Number.NaN;
  const numRating = !Number.isNaN(ratingValue) && Number.isFinite(ratingValue) ? ratingValue : 0;
  const hasRating = numRating > 0;

  // Format date
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // Get initials
  const getInitials = (displayName: string) => {
    if (!displayName) return '?';
    return displayName
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Parse categories
  const categoryList = Array.isArray(categories) ? categories : categories ? [categories] : [];

  // Pre-compute theme-based colors
  const bgColor = isDark ? '#1a1f2e' : '#ffffff';
  const dividerColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)';

  return (
    <Drawer
      key={`drawer-${isDark ? 'dark' : 'light'}`}
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      hideBackdrop
      disablePortal
      PaperProps={{
        sx: {
          width: { xs: 360, md: 420 },
          backgroundColor: bgColor,
          backgroundImage: 'none',
          top: '64px',
          height: 'calc(100% - 64px)',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #00d4aa 0%, #00a884 100%)',
          p: 3,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              backgroundColor: 'rgba(255,255,255,0.2)',
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'white',
            }}
          >
            {getInitials(name)}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, lineHeight: 1.2 }}>
              {name || 'Unknown Business'}
            </Typography>
            {hasRating && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <Rating
                  value={numRating}
                  precision={0.5}
                  size="small"
                  readOnly
                  sx={{ '& .MuiRating-iconFilled': { color: '#fff' } }}
                />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  ({numRating.toFixed(1)})
                </Typography>
              </Box>
            )}
            {categoryList.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {categoryList.slice(0, 2).map(cat => (
                  <Chip
                    key={`cat-${String(cat)}`}
                    label={cat}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontSize: '0.7rem',
                      height: 22,
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', pb: 3 }}>
        {/* Pipeline Status */}
        {lead.status && (
          <>
            <SectionHeader title="Pipeline Status" />
            <Box sx={{ px: 2, pb: 1 }}>
              <LeadStatusBadge status={lead.status} size="md" />
              {lead.statusChangedAt && (
                <Typography
                  variant="caption"
                  sx={{ ml: 1, color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.38)' }}
                >
                  since {formatDate(lead.statusChangedAt)}
                </Typography>
              )}
            </Box>
            <Divider sx={{ my: 2, borderColor: dividerColor }} />
          </>
        )}

        {/* Contact Information */}
        <SectionHeader title="Contact Information" />
        <InfoRow
          icon={<PhoneIcon sx={{ color: '#4fc3f7', fontSize: 20 }} />}
          label="Phone"
          value={phone}
          color="#4fc3f7"
          copyable
          isDark={isDark}
        />
        <InfoRow
          icon={<EmailIcon sx={{ color: '#ba68c8', fontSize: 20 }} />}
          label="Email"
          value={email}
          color="#ba68c8"
          copyable
          isDark={isDark}
        />
        <InfoRow
          icon={<LocationOnIcon sx={{ color: '#ef5350', fontSize: 20 }} />}
          label="Address"
          value={fullAddress}
          color="#ef5350"
          copyable
          isDark={isDark}
        />
        <InfoRow
          icon={<LanguageIcon sx={{ color: '#90caf9', fontSize: 20 }} />}
          label="Website"
          value={website}
          color="#90caf9"
          copyable
          isDark={isDark}
        />
        {linkedinUrl && (
          <InfoRow
            icon={<LinkIcon sx={{ color: '#0077b5', fontSize: 20 }} />}
            label="LinkedIn"
            value={linkedinUrl}
            color="#0077b5"
            copyable
            isDark={isDark}
          />
        )}

        {/* Additional Contacts Section */}
        {(additionalContacts.length > 0 || additionalEmails.length > 0 || additionalPhones.length > 0) && (
          <Box sx={{ px: 2 }}>
            <AdditionalContacts
              leadId={leadId}
              additionalContacts={additionalContacts}
              additionalEmails={additionalEmails}
              additionalPhones={additionalPhones}
              isUnlocked={additionalContactsUnlocked}
              onUnlock={unlockSingleLead}
              userCredits={userCredits}
            />
          </Box>
        )}

        <Divider sx={{ my: 2, borderColor: dividerColor }} />

        {/* Business Details */}
        <SectionHeader title="Business Details" />
        <InfoRow
          icon={<BusinessIcon sx={{ color: '#00d4aa', fontSize: 20 }} />}
          label="Business Name"
          value={name}
          color="#00d4aa"
          isDark={isDark}
        />
        {company && company !== name && (
          <InfoRow
            icon={<BusinessIcon sx={{ color: '#ffb74d', fontSize: 20 }} />}
            label="Company"
            value={company}
            color="#ffb74d"
            isDark={isDark}
          />
        )}
        {position && (
          <InfoRow
            icon={<BusinessIcon sx={{ color: '#81c784', fontSize: 20 }} />}
            label="Position"
            value={position}
            color="#81c784"
            isDark={isDark}
          />
        )}
        {hasRating && (
          <InfoRow
            icon={<StarIcon sx={{ color: '#ffd700', fontSize: 20 }} />}
            label="Rating"
            value={
              reviewCount ? `${numRating.toFixed(1)} / 5.0 (${reviewCount} reviews)` : `${numRating.toFixed(1)} / 5.0`
            }
            color="#ffd700"
            isDark={isDark}
          />
        )}
        {categoryList.length > 0 && (
          <InfoRow
            icon={<CategoryIcon sx={{ color: '#ffb74d', fontSize: 20 }} />}
            label="Categories"
            value={categoryList.join(', ')}
            color="#ffb74d"
            isDark={isDark}
          />
        )}

        <Divider sx={{ my: 2, borderColor: dividerColor }} />

        {/* Source Information */}
        <SectionHeader title="Source Information" />
        <InfoRow
          icon={<LanguageIcon sx={{ color: '#90caf9', fontSize: 20 }} />}
          label="Source"
          value={source || scraperName}
          color="#90caf9"
          isDark={isDark}
        />
        {sourceUrl && (
          <InfoRow
            icon={<LinkIcon sx={{ color: '#ce93d8', fontSize: 20 }} />}
            label="Source URL"
            value={sourceUrl}
            color="#ce93d8"
            copyable
            isDark={isDark}
          />
        )}
        <InfoRow
          icon={<CampaignIcon sx={{ color: '#4db6ac', fontSize: 20 }} />}
          label="Campaign"
          value={campaignName}
          color="#4db6ac"
          isDark={isDark}
        />
        <InfoRow
          icon={<CalendarTodayIcon sx={{ color: '#aed581', fontSize: 20 }} />}
          label="Scraped At"
          value={formatDate(scrapedAt || createdAt)}
          color="#aed581"
          isDark={isDark}
        />
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: `1px solid ${dividerColor}` }}>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            width: '100%',
            py: 1.5,
            backgroundColor: '#00d4aa',
            color: '#1a1f2e',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.9rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: '#00e5b8',
              transform: 'translateY(-1px)',
            },
          }}
        >
          Close
        </Box>
      </Box>
    </Drawer>
  );
};

export default LeadDetailsDrawer;
